import type { MouseEvent } from 'react';
import type { MenuItem } from '@cracked-mug/shared';
import { formatPrice } from '@cracked-mug/shared';
import { Heart, Star } from 'lucide-react';
import { cn } from '../../lib/cn';
import { useFavorites } from '../../hooks/useFavorites';

interface ItemCardProps {
  item: MenuItem;
  onSelect: (item: MenuItem) => void;
  /** Render the favorite heart. Off on the logged-out public menu, where
   *  there's no account to save favorites to. */
  showFavorite?: boolean;
}

/**
 * The chipped-mug placeholder mark (echoes the landing motif) shown for items
 * without a photo — a charcoal body with a gold cracked rim on warm chalk.
 */
function MugMark() {
  return (
    <svg viewBox="0 0 200 200" className="relative z-[1] h-14 w-14" fill="none" aria-hidden="true">
      <g
        stroke="rgba(26,26,26,0.32)"
        strokeWidth="5.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M 50 62 L 50 150 Q 50 168 68 168 L 122 168 Q 140 168 140 150 L 140 62" />
        <path d="M 140 82 Q 178 82 178 108 Q 178 134 140 134" />
      </g>
      <path
        d="M 50 62 L 60 58 L 70 64 L 82 56 L 95 64 L 108 56 L 122 64 L 132 56 L 140 62"
        stroke="#d4a843"
        strokeWidth="5.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <line
        x1="62"
        y1="86"
        x2="96"
        y2="86"
        stroke="rgba(26,26,26,0.15)"
        strokeWidth="4.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

export function ItemCard({ item, onSelect, showFavorite = true }: ItemCardProps) {
  const disabled = !item.isAvailable;
  const { isFavorite, toggle } = useFavorites();
  const favorited = isFavorite(item.itemId);

  const handleFavoriteClick = (e: MouseEvent<HTMLButtonElement>): void => {
    e.stopPropagation();
    void toggle(item.itemId);
  };

  return (
    <div
      className={cn(
        'group relative transition-transform duration-200',
        !disabled && 'hover:-translate-y-1',
      )}
    >
      <button
        type="button"
        onClick={() => onSelect(item)}
        disabled={disabled}
        className={cn(
          'block w-full overflow-hidden rounded-2xl border border-wca-black/[0.07] bg-wca-surface text-left shadow-card transition-shadow',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-wca-red',
          disabled ? 'cursor-not-allowed' : 'group-hover:shadow-card-lg active:scale-[0.99]',
        )}
      >
        <div className="relative flex aspect-[5/3] items-center justify-center overflow-hidden border-b border-wca-black/5">
          {item.imageUrl !== null ? (
            <img src={item.imageUrl} alt={item.name} className="h-full w-full object-cover" />
          ) : (
            <div className="cm-thumb-chalk absolute inset-0 flex items-center justify-center">
              <MugMark />
            </div>
          )}
          {disabled && (
            <>
              <div className="absolute inset-0 bg-wca-chalk/60" />
              <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-wca-surface/90 px-3 py-1.5 font-mono text-[11px] font-semibold uppercase tracking-[0.12em] text-wca-black shadow-card">
                Sold out today
              </span>
            </>
          )}
        </div>

        <div className={cn('p-4', disabled && 'opacity-55')}>
          <div className="flex items-baseline justify-between gap-2.5">
            <h3 className="font-display text-lg font-bold leading-tight tracking-tight">
              {item.name}
            </h3>
            <p className="whitespace-nowrap font-mono text-[15px] font-semibold tabular-nums text-wca-red">
              {formatPrice(item.basePriceCents)}
            </p>
          </div>
          <p className="mt-1.5 line-clamp-2 text-[13px] leading-relaxed text-wca-gray-mid">
            {item.description}
          </p>
          {item.isSeasonal && (
            <span className="mt-2.5 inline-flex items-center gap-1 rounded-full bg-wca-gold/15 px-2 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-[0.08em] text-[#8a6d1f]">
              <Star size={10} className="fill-current" />
              Seasonal
            </span>
          )}
        </div>
      </button>

      {/* Favorite toggle — separate element since buttons can't nest. Hidden on
          the logged-out public menu (no account to save to). */}
      {showFavorite && (
        <button
          type="button"
          onClick={handleFavoriteClick}
          aria-label={
            favorited ? `Remove ${item.name} from favorites` : `Add ${item.name} to favorites`
          }
          aria-pressed={favorited}
          className="absolute right-2.5 top-2.5 z-10 inline-flex h-9 w-9 items-center justify-center rounded-full bg-wca-surface/90 text-wca-gray-mid shadow-card backdrop-blur-sm transition-colors hover:text-wca-red focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-wca-red"
        >
          <Heart size={18} className={favorited ? 'fill-wca-red text-wca-red' : ''} />
        </button>
      )}
    </div>
  );
}
